import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Keyboard, Zap, Search, Wallet, Compass } from "lucide-react";

// Grupos organizados para que el taquillero encuentre lo que necesita al instante.
const GROUPS = [
  {
    label: "Emisión",
    icon: Zap,
    tint: "text-primary",
    items: [
      { keys: ["0-9"], desc: "Escribir número o monto" },
      { keys: ["+"], desc: "Agregar jugada al ticket" },
      { keys: ["Enter", "F8"], desc: "Emitir ticket" },
      { keys: ["Tab"], desc: "Alternar campo Número ↔ Monto" },
      { keys: [".", ","], desc: "Separador decimal (en Monto)" },
      { keys: ["Backspace"], desc: "Borrar último dígito" },
      { keys: ["N"], desc: "Foco al campo Número" },
      { keys: ["T"], desc: "Foco al campo Monto" },
      { keys: ["F2"], desc: "Cambiar moneda Bs ↔ USD" },
      { keys: ["F12"], desc: "Ir al selector de signo (cuando aplica)" },
    ],
  },
  {
    label: "Selección de sorteos",
    icon: Compass,
    tint: "text-teal-300",
    items: [
      { keys: ["Ctrl", "1-9"], desc: "Alternar lotería N° 1-9" },
      { keys: ["F9"], desc: "Alternar vista Cuadrícula / Lista cronológica" },
      { keys: ["↑", "↓"], desc: "Navegar jugadas del ticket" },
      { keys: ["Delete", "E"], desc: "Eliminar jugada seleccionada" },
      { keys: ["L", "F10"], desc: "Limpiar ticket completo" },
    ],
  },
  {
    label: "Consulta e Impresión",
    icon: Search,
    tint: "text-sky-300",
    items: [
      { keys: ["F3"], desc: "Repetir ticket por serial" },
      { keys: ["F4"], desc: "Reimprimir por serial" },
      { keys: ["F5"], desc: "Anular último ticket" },
      { keys: ["F6"], desc: "Anular por serial" },
    ],
  },
  {
    label: "Caja y Ayuda",
    icon: Wallet,
    tint: "text-amber-300",
    items: [
      { keys: ["F7"], desc: "Abrir / Cerrar caja" },
      { keys: ["F1"], desc: "Mostrar / ocultar esta ayuda" },
      { keys: ["Esc"], desc: "Cerrar diálogos" },
    ],
  },
];

function Kbd({ children }) {
  return (
    <kbd className="inline-flex items-center px-2 py-0.5 rounded-md border border-border bg-secondary/70 text-foreground font-mono text-[11px] font-semibold shadow-sm">
      {children}
    </kbd>
  );
}

export default function ShortcutsHelpOverlay({ open, onClose }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="bg-card border-border max-w-3xl"
        data-testid="pos-shortcuts-help-overlay"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Keyboard size={18} className="text-primary" />
            Atajos de teclado del POS
            <span className="ml-2 text-xs font-normal text-muted-foreground">
              Pulsa <Kbd>F1</Kbd> o <Kbd>Esc</Kbd> para cerrar
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-1">
          {GROUPS.map((g) => {
            const Icon = g.icon;
            return (
              <div
                key={g.label}
                className="rounded-lg border border-border bg-secondary/30 p-3"
                data-testid={`pos-shortcuts-group-${g.label}`}
              >
                <div className={`flex items-center gap-1.5 text-xs uppercase tracking-widest mb-2 ${g.tint}`}>
                  <Icon size={13} /> {g.label}
                </div>
                <ul className="space-y-1.5">
                  {g.items.map((it, i) => (
                    <li key={i} className="flex items-center justify-between gap-3 text-sm">
                      <div className="flex items-center gap-1 flex-wrap">
                        {it.keys.map((k, j) => (
                          <span key={j} className="flex items-center gap-1">
                            <Kbd>{k}</Kbd>
                            {j < it.keys.length - 1 && (
                              <span className="text-muted-foreground text-[10px]">o</span>
                            )}
                          </span>
                        ))}
                      </div>
                      <span className="text-muted-foreground text-xs text-right leading-tight">
                        {it.desc}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        <div className="text-[11px] text-muted-foreground mt-2 border-t border-border pt-2">
          Consejo: el <b>último monto</b> se guarda automáticamente. Escribe solo el número y pulsa <Kbd>+</Kbd> para
          agregar la jugada con el mismo monto anterior — ideal para rachas rápidas.
        </div>
      </DialogContent>
    </Dialog>
  );
}
